import Component from '@glimmer/component';
import { service } from '@ember/service';
import { variation } from 'ember-launch-darkly';
import { GEOS } from 'core/utils/constants';
import { eq } from 'ember-truth-helpers';
import { get } from '@ember/helper';
import { t } from 'ember-intl';
import {
  HdsFormSuperSelectSingleField,
  HdsIcon,
  HdsAppFooter,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import { FlexGrid, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import * as cloudUiIndex from 'core/utils/cloud-ui-index';
import Products from 'hcp/components/hcp-marketing/products';

const { EU, US } = GEOS;
const {
  DEVELOPMENT,
  DEVELOPMENT_EU,
  INTEGRATION,
  INTEGRATION_EU,
  PRODUCTION,
  PRODUCTION_EU,
} = cloudUiIndex;

const ENVIRONMENT_URLS = {
  development: {
    global: DEVELOPMENT.url,
    eu: DEVELOPMENT_EU.url,
  },
  integration: {
    global: INTEGRATION.url,
    eu: INTEGRATION_EU.url,
  },
  production: {
    global: PRODUCTION.url,
    eu: PRODUCTION_EU.url,
  },
};

/**
 *
 * `HcpMarketingMain` component is used to display HashiCorp Cloud Platform
 * branding and marketing information. It does not take any arguments.
 * All content is located within the component itself.
 *
 *
 * ```
 * <HcpMarketing::Main />
 * ```
 *
 * @class HcpMarketingMain
 *
 */
export default class HcpMarketingMain extends Component {
  @service config;
  @service systemStatus;
  @service intl;

  EU = EU;

  get statusPageUrl() {
    return this.systemStatus.statusPageUrl;
  }

  get hcpStatus() {
    return this.systemStatus.hcp;
  }

  get deployGeo() {
    return this.config.app?.deployGeo ?? US;
  }

  get geoOptions() {
    const baseOptions = [
      {
        text: this.intl.t(
          'components.marketing.landing-page.geo-picker.europe'
        ),
        icon: 'globe',
        value: EU,
      },
      {
        text: this.intl.t(
          'components.marketing.landing-page.geo-picker.global'
        ),
        icon: 'globe',
        value: US,
      },
    ];

    if (variation('hcp-ui-geo-switcher-learn-more-enabled')) {
      baseOptions.push({
        text: this.intl.t(
          'components.marketing.landing-page.geo-picker.learn-more.label'
        ),
        icon: 'external-link',
        value: '',
        url: this.intl.t(
          'components.marketing.landing-page.geo-picker.learn-more.url'
        ),
      });
    }

    return baseOptions;
  }

  get geoSwitcherEnabled() {
    return this.deployGeo === EU || variation('hcp-ui-geo-switcher-global');
  }

  redirect = (url, target = '_blank') => {
    window.open(url, target);
  };

  changeGeo = (option) => {
    if (option?.url) {
      this.redirect(option.url);
      return;
    }

    const geo = option.value;
    const env = this.config.app?.deployTarget ?? 'production';

    const targetUrl =
      ENVIRONMENT_URLS[env]?.[geo] || ENVIRONMENT_URLS[env]?.global;

    if (targetUrl) {
      window.location.assign(targetUrl);
    }
  };
  <template>
    <main
      data-test-page-hcp-marketing-main
      class="hcp-marketing-main"
      ...attributes
    >
      {{! Header }}
      <header class="hcp-marketing-main__header">
        {{#if (has-block "logo")}}
          {{yield to="logo"}}
        {{else}}
          <FlexGrid as |G|>
            <G.Item @xs="9" @lg="9">
              <div class="logo">
                <img
                  src="/images/logos/HCP-Horizontal_onLight.svg"
                  width="283"
                  height="36"
                  aria-hidden="true"
                />
              </div>
            </G.Item>
            {{#let (eq this.deployGeo this.EU) as |isEU|}}
              {{#if this.geoSwitcherEnabled}}
                <G.Item @xs="3" @lg="3">
                  <HdsFormSuperSelectSingleField
                    @onChange={{this.changeGeo}}
                    @options={{this.geoOptions}}
                    @selected={{if
                      isEU
                      (get this.geoOptions 0)
                      (get this.geoOptions 1)
                    }}
                    data-test-page-hcp-marketing-main-geo-picker
                    as |F|
                  >
                    <F.Label data-test-page-hcp-marketing-main-geo-picker-label>
                      {{t "components.marketing.landing-page.geo-picker.title"}}
                    </F.Label>
                    <F.Options>
                      {{#let F.options as |option|}}
                        {{#if option.url}}
                          <Flex
                            @justifyContent="start"
                            @alignItems="top"
                            @gap="sm"
                          >
                            <HdsTextBody
                              @tag="span"
                              @weight="regular"
                              class="hcp-marketing-main__link-label"
                              data-test-page-hcp-marketing-main-geo-picker-link
                            >{{option.text}}</HdsTextBody>
                            <HdsIcon
                              class="hcp-marketing-main__link-icon"
                              @name={{option.icon}}
                            />
                          </Flex>
                        {{else}}
                          <Flex
                            @justifyContent="start"
                            @alignItems="center"
                            @gap="xs"
                          >
                            <HdsIcon @name={{option.icon}} />
                            <HdsTextBody
                              @tag="span"
                              @weight="regular"
                              data-test-page-hcp-marketing-main-geo-picker-option
                            >{{option.text}}</HdsTextBody>
                          </Flex>
                        {{/if}}
                      {{/let}}
                    </F.Options>
                  </HdsFormSuperSelectSingleField>
                </G.Item>
              {{/if}}
            {{/let}}
          </FlexGrid>
          <div {{boxPadding "lg 0"}}>
            <hr />
          </div>
          <Products />
        {{/if}}
      </header>
      {{! Content }}
      <div
        class="hcp-marketing-main__content"
        data-test-page-hcp-marketing-main-content
      >
        <Flex
          @alignItems="flex-start"
          @basis="content"
          @direction="column"
          @gap="xs"
          @justifyContent="flex-start"
          @grow={{1}}
          @wrap={{true}}
        />
        {{yield}}
      </div>

      <HdsAppFooter data-test-page-hcp-marketing-main-footer as |AF|>
        {{#if this.hcpStatus}}
          <AF.StatusLink
            @status={{this.hcpStatus}}
            @text={{t
              "components.hcp.status-page-indicator-link"
              status=this.hcpStatus
            }}
            data-test-link-status-page
          />
        {{else}}
          <AF.StatusLink
            @text={{t "components.hcp.status-page-link"}}
            href={{this.statusPageUrl}}
            data-test-link-status-page
          />
        {{/if}}
        <AF.Link
          @href="https://eula.hashicorp.com/OnlineAgreements.pdf"
          @icon="external-link"
          rel="noreferrer noopener"
          target="_blank"
          data-test-link-terms
        >
          {{t "components.layout.footer.terms-of-service-long"}}
        </AF.Link>
        <AF.Link
          @href="https://www.hashicorp.com/privacy"
          @icon="external-link"
          @isHrefExternal={{true}}
          rel="noreferrer noopener"
          target="_blank"
          data-test-link-privacy
        >
          {{t "components.layout.footer.privacy-long"}}
        </AF.Link>
        <AF.Link
          @href="https://developer.hashicorp.com/hcp/docs"
          @icon="external-link"
          @isHrefExternal={{true}}
          rel="noreferrer noopener"
          target="_blank"
          data-test-link-documentation
        >
          {{t "components.hcp.footer.footer-link.documentation"}}
        </AF.Link>
      </HdsAppFooter>
    </main>
  </template>
}
